import { create } from 'zustand';
import { isStore, type Store } from '../lib/store.js';

/**
 * Le magasin affiché sur `/courses`.
 * Décisions : [ADR 0011](../../../docs/adr/0011-deux-magasins-courses-catalogue.md) § 6.
 *
 * Ce store ne fait que **porter** la valeur. Les stores `courses` et `catalog` viennent la
 * lire au moment de charger ; ils ne la reçoivent pas en argument, parce que `drain()`
 * ([lib/offline/replay.ts](../lib/offline/replay.ts)) appelle `load()` sans rien savoir de
 * l'écran. La dépendance reste à sens unique : courses → shop, jamais l'inverse.
 */

const STORAGE_KEY = 'nota_store';

/** Toute lecture est défensive : en navigation privée, `localStorage` peut jeter. On
 * retombe alors sur le magasin par défaut, ce qui est un inconfort, pas une panne. */
function readPersisted(): Store {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return isStore(raw) ? raw : 'lafourche';
  } catch {
    return 'lafourche';
  }
}

interface ShopState {
  store: Store;
  setStore: (store: Store) => void;
}

export const useShop = create<ShopState>((set) => ({
  store: readPersisted(),
  setStore: (store) => {
    try {
      localStorage.setItem(STORAGE_KEY, store);
    } catch {
      // Le choix ne survivra pas au rechargement, l'app marche quand même.
    }
    set({ store });
  },
}));

/** Pour les appelants hors React — les stores `courses` et `catalog`. */
export function currentStore(): Store {
  return useShop.getState().store;
}
